import { Button, FormField, FormSection } from '../common'
import TagChipPicker from './TagChipPicker'
import { buildRegulationOverride } from './regulationOverrides'
import styles from './PostComposer.module.css'

// Most posts read identically everywhere, so this stays collapsed and empty by
// default. It exists for the uncommon case an operator already knows about at
// authoring time: one regulation needs different wording or tagging.
export default function RegulationContentSection({
  regulations = [],
  tags = [],
  sharedText = '',
  sharedTagIds = [],
  overrides = {},
  onChange,
  disabled = false,
}) {
  // A post cannot give every regulation its own content: the server refuses to
  // leave the original serving none, so one always keeps the shared version.
  const sharedRemaining = regulations.filter((regulation) => !(regulation.value in overrides))
  const atOverrideLimit = sharedRemaining.length <= 1

  if (regulations.length < 2) return null

  const setOverride = (offeringId, patch) => onChange({
    ...overrides,
    [offeringId]: { ...overrides[offeringId], ...patch },
  })

  const addOverride = (offeringId) => onChange({
    ...overrides,
    [offeringId]: buildRegulationOverride(sharedText, sharedTagIds),
  })

  const removeOverride = (offeringId) => {
    const next = { ...overrides }
    delete next[offeringId]
    onChange(next)
  }

  const toggleTag = (offeringId, tagId) => {
    const current = overrides[offeringId]?.tagIds || []
    setOverride(offeringId, {
      tagIds: current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId],
    })
  }

  return (
    <FormSection
      title="Regulation-specific content"
      headingSize="subsection"
      collapsible
      defaultExpanded={Object.keys(overrides).length > 0}
    >
      <p className={styles.sectionHelper}>
        Every regulation uses the caption and tags above unless you override them here.
        Overrides are written when you publish now; after review, add them from Manage posts.
      </p>
      <ul className={styles.regulationCopyList}>
        {regulations.map((regulation) => {
          const override = overrides[regulation.value]
          const blockedReason = !override && atOverrideLimit
            ? 'One regulation has to keep the shared content.'
            : ''
          return (
            <li key={regulation.value} className={styles.regulationCopyRow}>
              <div className={styles.regulationCopyHeader}>
                <span className={styles.regulationCopyName}>{regulation.label}</span>
                {override ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={disabled}
                    onClick={() => removeOverride(regulation.value)}
                  >
                    Use shared content
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    disabled={disabled || atOverrideLimit}
                    title={blockedReason || undefined}
                    onClick={() => addOverride(regulation.value)}
                  >
                    Different content
                  </Button>
                )}
              </div>
              {override ? (
                <>
                  <FormField
                    label={`Caption for ${regulation.label}`}
                    name={`regulation-copy-${regulation.value}`}
                    type="textarea"
                    rows={3}
                    value={override.text}
                    disabled={disabled}
                    onChange={(event) => setOverride(regulation.value, { text: event.target.value })}
                  />
                  <div
                    className={styles.regulationTagGroup}
                    role="group"
                    aria-label={`Tags for ${regulation.label}`}
                  >
                    <span className={styles.regulationTagLabel}>Tags for {regulation.label}</span>
                    <TagChipPicker
                      tags={tags}
                      selectedTagIds={override.tagIds}
                      onToggle={(tagId) => toggleTag(regulation.value, tagId)}
                    />
                  </div>
                </>
              ) : null}
            </li>
          )
        })}
      </ul>
    </FormSection>
  )
}
